// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import cartLineSharedStyles from "@conklin/App/SharedStyles/CartLineSharedStyles";
import { productListLineStyles } from "@conklin/Overrides/Widgets/MyListsDetails/MyListsDetailsProductListLine";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { displayFlexOnPrint, sharedProductDescriptionStyles, xScreen } from "@conklin/XDL/Utilities";
import { mergeWithCss } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { IconWrapper } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { gridWidth } from "@insite/mobius/utilities";
import { css } from "styled-components";

const priceTextStyles = {
    variant: "h4",
    css: css`
        margin-bottom: 0;
        display: inline-block;
        @media print {
            font-size: 14px;
        }
    `,
} as TypographyProps;

// ============== Checbox Section ===================//
productListLineStyles.selectedCheckbox = {
    css: css`
        @media print {
            display: none;
        }
        margin: 0;
    `,
};
// ================================================== //

// ================ Image Section ===================== //
productListLineStyles.productImageStyles = {
    linkWrappingImage: {
        css: css`
            width: 120px;
            height: 120px;
            justify-content: center;
            ${xScreen("lte", "md")} {
                width: 91px;
                height: 91px;
            }
        `,
    },
    img: {
        css: css`
            width: auto;
            height: auto;
            max-height: 100%;
            max-width: 100%;
            object-fit: contain;
        `,
    },
};
// ================================================== //

// ================ Info Section ==================== //
productListLineStyles.productDescriptionStyles = {
    ...sharedProductDescriptionStyles(),
};

productListLineStyles.restrictedDescriptionText = {
    ...sharedProductDescriptionStyles().productDetailDescription,
};

productListLineStyles.partNumbersSection = {
    css: css`
        display: flex;
        justify-content: space-between;
        margin-bottom: 12px;
        flex-direction: column;
        row-gap: 8px;
        > ${StyledWrapper},>${TypographyStyle} {
            width: 100%;
        }
    `,
};

productListLineStyles.productPartNumbersStyles = {
    ...mergeWithCss(cartLineSharedStyles.productPartNumbers, {
        container: {
            css: css`
                margin-bottom: 0;
            `,
        },
    }),
};

productListLineStyles.sortOrderGridItem = {
    width: 12,
    css: css`
        @media print {
            display: none;
        }
    `,
};
productListLineStyles.sortOrderTextField = {
    labelPosition: "left",
    sizeVariant: "small",
    cssOverrides: {
        formInputWrapper: css`
            width: 60px;
        `,
    },
    labelProps: {
        css: css`
            width: auto;
        `,
    },
};

productListLineStyles.createdOnText = {
    size: 12,
    lineHeight: "14px",
    italic: true,
    weight: "normal",
    color: "common.ui4",
    css: css`
        display: block;
    `,
};

productListLineStyles.restrictedMessageText = {
    css: css`
        margin-top: 12px;
        ${xScreen("lte", "md")} {
            max-width: 255px;
            min-width: 255px;
        }
        ${xScreen("lte", "sm")} {
            max-width: unset;
            min-width: unset;
        }
    `,
};

// ================ Actions and price Section ================== //
productListLineStyles.quantityBreakPricing = {
    viewLink: {
        typographyProps: {
            size: 12,
        },
        css: css`
            margin-bottom: 8px;
        `,
    },
};

productListLineStyles.productPriceStyles = {
    wrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    priceWrapper: {
        css: css`
            line-height: 19px;
            margin-bottom: 0;
        `,
    },
    price: {
        priceText: priceTextStyles,
        errorText: priceTextStyles,
        signInText: priceTextStyles,
        unitOfMeasure: {
            unitOfMeasureText: {
                size: 12,
                lineHeight: "14px",
                weight: "normal",
                css: css`
                    white-space: nowrap;
                `,
            },
        },
    },
    invalidPriceText: priceTextStyles,
    quoteMessage: {
        text: priceTextStyles,
        tooltip: {
            iconProps: {
                css: css`
                    display: none;
                `,
            },
        },
    },
};

productListLineStyles.addToCartButton = {
    mergeCss: true,
    sizeVariant: "small",
    css: css`
        flex: 1;
        max-width: 100%;
        width: 100%;
    `,
};

productListLineStyles.quantityAddtoCartWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        width: 100%;
        gap: 8px;
        @media print {
            flex-direction: column;
            align-items: flex-start;
            gap: 5px;
        }
    `,
};
productListLineStyles.updateQuantityLink = {
    typographyProps: {
        size: 12,
        weight: "bold",
        css: css`
            line-height: ${fontConfig.lineHeight.small};
        `,
    },
};
productListLineStyles.quantityUpdatedWrapper = {
    css: css`
        line-height: ${fontConfig.lineHeight.small};
        @media print {
            display: none;
        }
    `,
};
productListLineStyles.quantityUpdatedIcon = {
    src: Check,
    color: "success",
    size: 11,
    css: css`
        margin-right: 3px;
    `,
};
productListLineStyles.quantityUpdatedText = {
    size: 12,
    lineHeight: "14px",
    weight: "bold",
    color: "success",
};

productListLineStyles.productAvailabilityStyles = {
    container: {
        css: css`
            margin-bottom: 8px;
        `,
        offsetCss: css`
            & > div:empty {
                display: none;
            }
        `,
    },
    errorWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    messageGridItem: {
        width: 6,
        css: css`
            ${displayFlexOnPrint}
        `,
    },
    availabilityByWarehouseLinkGridItem: {
        width: 6,
    },
};

productListLineStyles.productUnitOfMeasureSelectStyles = {
    labelProps: {
        css: css`
            display: none;
            @media print {
                display: block;
            }
        `,
    },
    sizeVariant: "small",
    cssOverrides: {
        inputSelect: css`
            margin: 0;
        `,
        formField: css`
            width: 90px;
            ${IconWrapper} {
                background: none;
            }
        `,
    },
};

const deleteLink = {
    icon: {
        iconProps: {
            src: X,
            size: 12,
            css: css`
                /* margin: 0 auto; */
                margin-right: 6px;
            `,
        },
    },
    typographyProps: {
        size: 12,
        lineHeight: "15px",
        css: css`
            &&& {
                line-height: 15px;
            }
        `,
    },
    css: css`
        display: flex;
        justify-content: center;
        margin-left: auto;
        ${xScreen("lte", "md")} {
            margin-top: 12px;
        }
        &:focus {
            outline: none;
        }
    `,
};

productListLineStyles.deleteLink = deleteLink;

productListLineStyles.deleteRestrictedLink = deleteLink;

productListLineStyles.deleteItemWrapper = {
    css: css`
        display: flex;
        align-self: flex-end;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;
        line-height: 15px;
        ${xScreen("lte", "md")} {
            margin-top: 0;
            flex-direction: row;
        }
    `,
};

productListLineStyles.updateQTY = {
    css: css`
        line-height: 12px;
        ${xScreen("lte", "md")} {
            margin-top: 12px;
            margin-left: auto;
        }
    `,
};

productListLineStyles.editNotesLink = {
    icon: {
        iconProps: {
            size: 12,
            css: css`
                margin-right: 6px;
            `,
        },
    },
    typographyProps: {
        size: 12,
        css: css`
            &&& {
                line-height: 15px;
            }
        `,
    },
    css: css`
        && {
            display: flex;
            justify-content: center;
        }
        ${xScreen("lte", "md")} {
            margin-top: 12px;
        }
    `,
};

productListLineStyles.purchasedDateGridItem = {
    width: 12,
    css: css`
        padding: 0 0 8px 0;
        gap: 4px;
    `,
};

productListLineStyles.purchasedDateLabelText = {
    ...mergeWithCss(productListLineStyles.purchasedDateLabelText, {
        size: 12,
        weight: fontConfig.weight.regular,
        transform: "mone",
        css: css`
            margin-bottom: 0;
            line-height: 15px;
        `,
    }),
};

productListLineStyles.purchasedDateText = {
    size: 12,
    css: css`
        line-height: 15px;
        margin-bottom: 0;
    `,
};

productListLineStyles.listLineContainer = {
    deleteItem: {
        css: css`
            grid-row: 4 / 6;
        `,
    },
};

// ==================== Notes Section ============================ //
productListLineStyles.editableNotesTextArea = {
    css: css`
        &&&& {
            resize: vertical;
            overflow: auto;
            @media print {
                border: 0;
                overflow: hidden;
                max-width: 100%;
                box-shadow: none;
            }
        }
        max-width: 50%;
        ${xScreen("lte", "md")} {
            min-width: 100%;
            max-width: 100%;
        }
    `,
    labelProps: {
        css: css`
            @media print {
                text-align: left;
                font-weight: ${fontConfig.weight.regular};
            }
        `,
    },
};

productListLineStyles.notesStyles = {
    size: 14,
    weight: "normal",
    color: "common.ui4",
    css: css`
        letter-spacing: 0;
        line-height: 19px;
    `,
};
// ===================================================== //
